import React, { useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { BrowseStateContext } from '@/utils/browseState';

const visiblePost = () => Array.from(document.querySelectorAll('[data-post-id]')).find(node => {
  const rect = node.getBoundingClientRect();
  return rect.bottom > 64 && rect.top < window.innerHeight;
});

const BrowsingSession = ({ children }) => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const entries = useRef(new Map());
  if (!entries.current.has(location.key)) {
    entries.current.set(location.key, { values: new Map(), scroll: { x: 0, y: 0 }, anchor: null, saved: false });
    if (entries.current.size > 20) entries.current.delete(entries.current.keys().next().value);
  }
  const entry = entries.current.get(location.key);
  // 经常返回的列表保持在最近使用的快照中，连续点开多个帖子也不会淘汰它。
  entries.current.delete(location.key);
  entries.current.set(location.key, entry);
  entry.entries = entries.current;

  useLayoutEffect(() => {
    const original = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = original; };
  }, []);

  useLayoutEffect(() => {
    let restoring = navigationType === 'POP' && entry.saved;
    let frame; let observer; let mutations;
    const save = () => {
      if (restoring) return;
      entry.scroll = { x: window.scrollX, y: window.scrollY };
      entry.saved = true;
      const post = visiblePost();
      entry.anchor = post ? { id: post.dataset.postId, top: post.getBoundingClientRect().top } : null;
    };
    const restore = () => {
      if (!restoring) return;
      // 图片或异步列表撑开页面时，继续对齐原卡片在视口中的位置。
      const post = entry.anchor && Array.from(document.querySelectorAll('[data-post-id]'))
        .find(node => node.dataset.postId === entry.anchor.id);
      const y = post ? window.scrollY + post.getBoundingClientRect().top - entry.anchor.top : entry.scroll.y;
      window.scrollTo({ left: entry.scroll.x, top: Math.max(0, y), behavior: 'instant' });
    };
    const stop = () => {
      restoring = false;
      cancelAnimationFrame(frame);
      observer?.disconnect(); mutations?.disconnect();
      document.removeEventListener('load', restore, true);
    };
    const onKey = event => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) stop();
    };
    if (restoring) {
      restore();
      frame = requestAnimationFrame(restore);
      if (typeof ResizeObserver !== 'undefined') {
        observer = new ResizeObserver(restore);
        observer.observe(document.body);
      }
      mutations = new MutationObserver(restore);
      mutations.observe(document.body, { childList: true, subtree: true });
      document.addEventListener('load', restore, true);
    } else window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
    const capture = () => { stop(); save(); };
    window.addEventListener('scroll', save, { passive: true });
    document.addEventListener('click', capture, true);
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchmove', stop, { passive: true });
    window.addEventListener('pointerdown', stop, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      stop();
      window.removeEventListener('scroll', save);
      document.removeEventListener('click', capture, true);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchmove', stop);
      window.removeEventListener('pointerdown', stop);
      window.removeEventListener('keydown', onKey);
    };
  }, [entry, navigationType]);

  return <BrowseStateContext.Provider value={entry}>{children}</BrowseStateContext.Provider>;
};

// 不持久化定位/私有列表到磁盘，退出或切换账号立即清空快照。
const BrowseHistory = ({ children }) => {
  const { isAuthenticated, userInfo, sessionVersion } = useSelector(state => state.user);
  return <BrowsingSession key={`${isAuthenticated}:${userInfo?.id}:${sessionVersion}:${userInfo?.school}`}>{children}</BrowsingSession>;
};

export default BrowseHistory;
